import { useId } from 'react';
import { Combobox, Input, Select } from '@wundo/ui';
import {
  meetingTypeIsAChoice,
  meetingTypeOptions,
} from '@/components/meetings/meetingTypeChoice.ts';
import type { MeetingTemplateItem } from '@wundo/shared/meetingTemplate.ts';
import { m } from '@/paraglide/messages.js';

/** The one conversation on screen, as its fields hold it. */
export interface FirstMeetingDraft {
  title: string;
  /** Who it is with, by name. Empty is an answer: naming nobody is allowed. */
  attendees: string[];
  day: string;
  hour: string;
  /**
   * Empty until the user picks one. It stays empty for an organisation with a
   * single gesprekstype: that one is filled in when the form is read, so the
   * screen keeps its other fields.
   */
  templateId: string;
}

interface FirstMeetingFieldsProps {
  draft: FirstMeetingDraft;
  /** The gesprekstypes this user can pick from. */
  templates: MeetingTemplateItem[];
  onChange: (patch: Partial<FirstMeetingDraft>) => void;
}

/** The same questions the agenda's dialog asks: a title, who, a day, an hour. */
export function FirstMeetingFields({
  draft,
  templates,
  onChange,
}: FirstMeetingFieldsProps) {
  const peopleId = useId();

  /*
   * With one gesprekstype the form stays a title, a day and an hour, and that
   * one type is applied silently. From two onwards the dropdown appears,
   * because nothing about a meeting typed in by hand tells Wundo which
   * structure it should follow.
   */
  const asksForType = meetingTypeIsAChoice(templates);

  /*
   * A date and a time field are as wide as the browser draws them — around
   * 9.5rem and 8rem with their picker icons — and the form sits in half a
   * card. Title, day and hour side by side would leave the title a stub, so
   * the title owns a line of its own and the moment sits under it.
   */
  return (
    <div className='flex flex-col gap-2.5'>
      <label className='flex min-w-0 flex-col gap-1.5'>
        <span className='text-sm font-semibold text-ink'>
          {m.onboarding_first_meeting_title_label()}
        </span>
        <Input
          aria-label={m.onboarding_first_meeting_title_label()}
          placeholder={m.onboarding_first_meeting_title_placeholder()}
          value={draft.title}
          onChange={(event) => onChange({ title: event.target.value })}
        />
      </label>
      {/*
        Right under the title, because it is the same question continued:
        what the conversation is, and who it is with. Nothing is offered to
        pick from — somebody in the wizard has no colleagues Wundo knows of
        yet, and a name typed here for the first time is the whole point.
      */}
      <div className='flex min-w-0 flex-col gap-1.5'>
        <label htmlFor={peopleId} className='text-sm font-semibold text-ink'>
          {m.manual_meeting_with_label()}{' '}
          <span className='font-normal text-ink-muted'>
            {m.manual_meeting_with_optional()}
          </span>
        </label>
        <Combobox
          id={peopleId}
          values={draft.attendees}
          onValuesChange={(attendees) => onChange({ attendees })}
          suggestions={[]}
          placeholder={m.manual_meeting_with_placeholder()}
          removeLabel={(person) => m.manual_meeting_with_remove({ name: person })}
        />
        <p className='text-xs text-ink-muted'>{m.manual_meeting_with_hint()}</p>
      </div>
      <div className='flex flex-wrap items-end gap-2.5'>
        <label className='flex min-w-40 flex-1 flex-col gap-1.5'>
          <span className='text-sm font-semibold text-ink'>
            {m.manual_meeting_day_label()}
          </span>
          <Input
            type='date'
            aria-label={m.manual_meeting_day_label()}
            value={draft.day}
            onChange={(event) => onChange({ day: event.target.value })}
          />
        </label>
        <label className='flex min-w-32 flex-1 flex-col gap-1.5'>
          <span className='text-sm font-semibold text-ink'>
            {m.manual_meeting_hour_label()}
          </span>
          <Input
            type='time'
            aria-label={m.manual_meeting_hour_label()}
            value={draft.hour}
            onChange={(event) => onChange({ hour: event.target.value })}
          />
        </label>
      </div>
      {/*
        Only when there is something to choose between. With a single
        gesprekstype the form keeps its three fields and that type is
        applied without ever being mentioned.
      */}
      {asksForType && (
        <label className='flex min-w-0 flex-col gap-1.5'>
          <span className='text-sm font-semibold text-ink'>
            {m.manual_meeting_type_label()}
          </span>
          <Select
            aria-label={m.manual_meeting_type_label()}
            wrapperClassName='w-full'
            className='w-full'
            value={draft.templateId}
            options={meetingTypeOptions(templates, draft.templateId)}
            onChange={(event) => onChange({ templateId: event.target.value })}
          />
        </label>
      )}
    </div>
  );
}
